import { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { FormField, Input } from '../../../components/ui/FormField';
import { agentHooks } from '../hooks';
import type { AgentDetail } from '../types';
import { useToast } from '../../../components/ui/Toast';

interface AgentStatusActionsProps {
  agent: AgentDetail;
}

export function AgentStatusActions({ agent }: AgentStatusActionsProps) {
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [reinstateOpen, setReinstateOpen] = useState(false);

  const [suspendReason, setSuspendReason] = useState('');
  
  const suspendMutation = agentHooks.useSuspend();
  const terminateMutation = agentHooks.useTerminate();
  const reinstateMutation = agentHooks.useReinstate();

  const { toast } = useToast();

  const handleSuspend = async () => {
    if (suspendReason.length < 20) {
      toast({
        title: 'Validation Error',
        description: 'Suspension reason must be at least 20 characters.',
        type: 'error',
      });
      return;
    }

    try {
      await suspendMutation.mutateAsync({ id: agent.id, body: { reason: suspendReason } });
      setSuspendOpen(false);
      setSuspendReason('');
      toast({ title: 'Agent Suspended', type: 'success' });
    } catch (e: any) {
      console.error('Failed to suspend agent', e);
      toast({
        title: 'Error suspending agent',
        description: e.message,
        type: 'error',
      });
    }
  };

  const handleTerminate = async () => {
    try {
      await terminateMutation.mutateAsync(agent.id);
      setTerminateOpen(false);
      toast({ title: 'Agent Terminated', type: 'success' });
    } catch (e: any) {
      console.error('Failed to terminate agent', e);
      // Check for OPEN_COLLECTIONS specific error
      if (e?.response?.data?.code === 'OPEN_COLLECTIONS' || e.message?.includes('OPEN_COLLECTIONS')) {
        toast({
          title: 'Cannot Terminate Agent',
          description: 'This agent has unreconciled cash receipts. Please reconcile or assign them to another agent before termination.',
          type: 'error',
        });
      } else {
        toast({
          title: 'Error terminating agent',
          description: e.message,
          type: 'error',
        });
      }
    }
  };

  const handleReinstate = async () => {
    try {
      await reinstateMutation.mutateAsync(agent.id);
      setReinstateOpen(false);
      toast({ title: 'Agent Reinstated', type: 'success' });
    } catch (e: any) {
      console.error('Failed to reinstate agent', e);
      toast({
        title: 'Error reinstating agent',
        description: e.message,
        type: 'error',
      });
    }
  };

  if (agent.status === 'TERMINATED') {
    return (
      <div className="text-sm text-text-muted">
        Terminated on {agent.terminatedAt ? new Date(agent.terminatedAt).toLocaleDateString() : 'Unknown date'}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {agent.status === 'ACTIVE' && (
        <Button variant="secondary" className="border-warning-500 text-warning-500 hover:bg-warning-50" onClick={() => setSuspendOpen(true)}>
          Suspend
        </Button>
      )}

      {agent.status === 'SUSPENDED' && (
        <Button variant="secondary" className="border-success-500 text-success-500 hover:bg-success-50" onClick={() => setReinstateOpen(true)}>
          Reinstate
        </Button>
      )}

      {(agent.status === 'ACTIVE' || agent.status === 'SUSPENDED') && (
        <Button variant="danger" onClick={() => setTerminateOpen(true)}>
          Terminate
        </Button>
      )}

      {/* Suspend Modal */}
      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend Agent</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-text-secondary">
              This will revoke the agent's active sessions immediately. They will not be able to log in.
            </p>
            <FormField label="Reason (min 20 chars)" htmlFor="suspendReason">
              <Input
                id="suspendReason"
                value={suspendReason}
                onChange={(e: any) => setSuspendReason(e.target.value)}
                placeholder="Detailed reason for suspension..."
              />
            </FormField>
            {suspendReason.length > 0 && suspendReason.length < 20 && (
              <em className="text-sm text-danger-500">Minimum 20 characters required.</em>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setSuspendOpen(false)}>Cancel</Button>
            <Button 
              variant="primary" 
              onClick={handleSuspend}
              disabled={suspendReason.length < 20 || suspendMutation.isPending}
            >
              Confirm Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate Modal */}
      <Dialog open={terminateOpen} onOpenChange={setTerminateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Terminate Agent</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-text-secondary">
              Are you sure you want to terminate <strong>{agent.fullName}</strong>? This action is permanent and their access will be revoked immediately.
            </p>
            <p className="text-sm text-danger-500 font-medium">
              Note: You cannot terminate an agent with open cash receipts.
            </p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTerminateOpen(false)}>Cancel</Button>
            <Button 
              variant="danger" 
              onClick={handleTerminate}
              disabled={terminateMutation.isPending}
            >
              Confirm Terminate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinstate Modal */}
      <Dialog open={reinstateOpen} onOpenChange={setReinstateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reinstate Agent</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm text-text-secondary">
              Are you sure you want to reinstate <strong>{agent.fullName}</strong>? They will regain access to their territory and assignments.
            </p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setReinstateOpen(false)}>Cancel</Button>
            <Button 
              variant="primary" 
              onClick={handleReinstate}
              disabled={reinstateMutation.isPending}
            >
              Confirm Reinstate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
